import React, { useEffect } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { Link, useSearchParams } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { API_BASE_URL } from '../config/api';
import SiteHeader from '../components/site/SiteHeader';
import SiteFooter from '../components/site/SiteFooter';
import { ROUTES } from '../components/site/siteNav';

/**
 * Sign In Page (Marketing v2)
 *
 * Public-facing sign-in for users arriving from marketing. Integrates with
 * Microsoft Entra External ID (CIAM) via the backend OAuth flow.
 *
 * Post-payment model: paid users are routed to /welcome (NOT the dashboard).
 * The DTA is the only entry point to the dashboard for end users. The
 * /dashboard/login route remains as an internal/dev backdoor, unlisted here.
 *
 * Flow:
 *  1. Already authed + paid → /welcome
 *  2. Already authed + not paid → /get-access
 *  3. Click "Sign in with Microsoft" → /auth/login → CIAM → returnUrl
 */
const SignInV2 = () => {
  usePageTitle('Sign in');
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get('plan');
  const returnUrlParam = searchParams.get('returnUrl');

  // If plan parameter exists, continue to /get-access with the plan so checkout auto-starts
  // post-login. Otherwise default to /welcome (paid-user landing) — the plan check on
  // mount below will route non-paying users to /get-access instead.
  const returnUrl = planParam
    ? `/get-access?plan=${planParam}`
    : (returnUrlParam || '/welcome');

  const { loading, authenticated, subscription } = useSubscription();

  /**
   * Redirect authenticated users
   */
  useEffect(() => {
    if (!loading && authenticated) {
      // Paid (active or past_due grace) → /welcome, where they can download the DTA
      const paid = subscription?.status === 'active' || subscription?.status === 'past_due';
      if (paid) {
        window.location.href = '/welcome';
      } else {
        // Otherwise, go to returnUrl (which includes plan parameter if present)
        window.location.href = returnUrl;
      }
    }
  }, [loading, authenticated, subscription, returnUrl]);

  // "Create an account" used to point at /get-access, the same place as the
  // View plans button above it, so it never started account creation. It now
  // opens CIAM on the sign-up form and comes back to the plans (10-29).
  // A brand-new account has no plan yet, so it comes back to the plans page
  // (with the plan, if one was chosen) rather than to /welcome and a bounce.
  const createAccountUrl = `${API_BASE_URL}/auth/login?signup=1&returnUrl=${encodeURIComponent(
    planParam ? `/get-access?plan=${planParam}` : '/get-access'
  )}`;

  /**
   * Initiate Microsoft sign-in flow
   */
  const handleSignIn = () => {
    const encodedReturnUrl = encodeURIComponent(returnUrl);
    window.location.href = `${API_BASE_URL}/auth/login?returnUrl=${encodedReturnUrl}`;
  };

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <div className="sticky top-0 z-50">
        <SiteHeader />
      </div>

      <main className="flex flex-1 items-start justify-center px-4 py-12 md:py-16">
        <div className="w-full max-w-[460px]">
          {loading ? (
            <div className="border border-black/25 px-8 py-16 text-center">
              <div className="mx-auto h-10 w-10 animate-spin rounded-full border-b-2 border-[#2F5F9E]" />
              <p className="mt-4 text-[15px] text-black/70">Checking authentication&hellip;</p>
            </div>
          ) : (
            <div className="border border-black/25 px-7 py-9 md:px-10 md:py-11">
              <h1 className="text-center text-[26px] font-bold text-black md:text-[32px]">
                Sign in
              </h1>
              <p className="mt-2 text-center text-[15px] text-black/70">
                Secure authentication powered by Microsoft
              </p>

              <button
                type="button"
                onClick={handleSignIn}
                className="mt-8 flex w-full items-center justify-center gap-3 rounded-[8px] bg-[#2F5F9E] px-6 py-3.5 text-[16px] text-white transition-colors hover:bg-[#27508a] md:text-[17px]"
              >
                <svg className="h-5 w-5" viewBox="0 0 21 21" fill="none" aria-hidden="true">
                  <rect x="1" y="1" width="9" height="9" fill="currentColor" />
                  <rect x="11" y="1" width="9" height="9" fill="currentColor" />
                  <rect x="1" y="11" width="9" height="9" fill="currentColor" />
                  <rect x="11" y="11" width="9" height="9" fill="currentColor" />
                </svg>
                Sign in with Microsoft
              </button>

              <div className="my-6 flex items-center gap-4">
                <span className="h-px flex-1 bg-black/20" />
                <span className="text-[13px] text-black/50">or</span>
                <span className="h-px flex-1 bg-black/20" />
              </div>

              <Link
                to={ROUTES.getStarted}
                className="block w-full rounded-[8px] border border-black/30 px-6 py-3.5 text-center text-[16px] text-black transition-colors hover:bg-black/[.04] md:text-[17px]"
              >
                View plans &amp; get access
              </Link>

              <p className="mt-7 text-center text-[15px] text-black/75">
                New to No / SCAM?{' '}
                <a href={createAccountUrl} className="text-[#2F5F9E] underline">
                  Create an account
                </a>
              </p>

              <p className="mt-2 text-center text-[14px]">
                <Link to={ROUTES.resources} className="text-[#2F5F9E] underline">
                  Need help?
                </Link>
              </p>
            </div>
          )}

          <p className="mt-6 text-center text-[13px] text-black/55">
            Use private browsing if this is not your device.{' '}
            <Link to={ROUTES.dataPrivacy} className="text-[#2F5F9E] underline">
              Learn more
            </Link>
          </p>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default SignInV2;
